import { LocationMarkerIcon } from "@heroicons/react/outline";
import { get, values } from "lodash";
import Tooltip from "rc-tooltip";
import React, { useEffect, useMemo } from "react";
import {
  matchPath,
  Redirect,
  Route,
  RouteComponentProps,
  Switch
} from "react-router-dom";
import DetailsPagination from "../../../components/DetailsPagination";
import Labels from "../../../components/labels/Labels";
import LocationActionsButton from "../../../components/LocationActionsButton";
import NavLink from "../../../components/NavLink";
import StatusDot from "../../../components/StatusDot";
import { useComments } from "../../../context/comments";
import { useFilteredList } from "../../../context/filteredList";
import { useInstalls } from "../../../context/installs";
import { useLocations } from "../../../context/locations";
import { useTransmitScales } from "../../../context/transmitScales";
import PrivateRoute from "../../../layout/PrivateRoute";
import { Entity } from "../../../utils/constants";
import {
  classificationColorMap,
  getClassification
} from "../../../utils/hotspotUtils";
import { LabelFilterKey } from "../../../utils/useFilteredLabels";
import useMapCenter from "../../../utils/useMapCenter";
import { useShouldUpdateMapCentroid } from "../../../utils/useShouldCenterMap";
import { isActiveInstall } from "../../Workspace/InstallsData/utils";
import RewardScalingTab from "../common/RewardScalingTab";
import LocationCommentsTab from "./LocationCommentsTab";
import LocationContactsTab from "./LocationContactsTab";
import LocationInfoTab from "./LocationInfoTab";
import LocationInstallsTab from "./LocationInstallsTab";
import SuperSimulationsTab from "./SuperSimulationsTab";

interface Props extends RouteComponentProps<{ id: string }> {}

const LocationDetails: React.FC<Props> = (props) => {
  const [center, setCenter, boundaries] = useMapCenter((state) => [
    state.center,
    state.setCenter,
    state.boundaries
  ]);
  const { currentLocation, locations, getLocationCoordinates } = useLocations();
  const { installs } = useInstalls();
  const { comments } = useComments();
  const { filteredList } = useFilteredList();
  const filteredLocations = useMemo(
    () => filteredList[Entity.location],
    [filteredList]
  );
  const { calculatedTransmitScales, isTransmitScaleActive } =
    useTransmitScales();

  const location = useMemo(
    () =>
      currentLocation ||
      (props.match.params.id ? locations[props.match.params.id] : undefined),
    [currentLocation, locations, props.match.params.id]
  );

  const commentsCount = useMemo(() => {
    if (!location) {
      return 0;
    }

    if (comments[location.uuid]) {
      return comments[location.uuid].length;
    }
  }, [comments, location]);

  const installsCount = useMemo(() => {
    if (!location) {
      return 0;
    }

    return values(installs).filter(
      (install) =>
        install.location_uuid === location.uuid && isActiveInstall(install)
    ).length;
  }, [location, installs]);

  const contactsCount = useMemo(() => {
    if (!location) {
      return 0;
    }

    return location.linked_contact_ids.length;
  }, [location]);

  const superSimulationsCount = useMemo(() => {
    if (!location) {
      return 0;
    }

    return location.super_simulations.length;
  }, [location]);

  const tabs = useMemo(() => {
    const allTabs = [
      {
        key: "super-simulations",
        title: "Super simulations",
        count: superSimulationsCount,
        hidden: false
      },
      {
        key: "reward-scaling",
        title: "Reward scaling",
        count: 0,
        hidden: false
      },
      {
        key: "contacts",
        title: "Contacts",
        count: contactsCount,
        hidden: false
      },
      {
        key: "installs",
        title: "Installs",
        count: installsCount,
        hidden: false
      },
      {
        key: "comments",
        title: "Comments",
        count: commentsCount,
        hidden: false
      },
      { key: "info", title: "Info", count: 0, hidden: false }
    ];

    return allTabs.filter(({ hidden }) => !hidden);
  }, [commentsCount, contactsCount, installsCount, superSimulationsCount]);

  const detailsPageMatch = matchPath<{
    id: string;
    section: string;
  }>(props.location.pathname, {
    path: `/locations/:id/:section`
  });

  const mapCentroid = useShouldUpdateMapCentroid({
    currentMapCentroid: center,
    selectedItem: {
      id: location?.uuid || "",
      coordinates: [location?.lng || 0, location?.lat || 0]
    },
    mapBoundaries: boundaries
  });

  useEffect(() => {
    setCenter(mapCentroid);
  }, [mapCentroid, setCenter]);

  const transmitScaleClassification = useMemo(
    () =>
      isTransmitScaleActive(props.match.params.id)
        ? getClassification(
            get(calculatedTransmitScales, props.match.params.id, 0)
          )
        : "disabled",
    [calculatedTransmitScales, isTransmitScaleActive, props.match.params.id]
  );

  const renderLabels = useMemo(() => {
    if (!location) {
      return (
        <span className="flex w-10 h-6 animate-pulse bg-gray-400 rounded sm:w-28 mt-4 mb-2" />
      );
    }

    return (
      <Labels
        className="mt-2"
        entity={Entity.location}
        linkedUuid={location.uuid}
        filterKey={LabelFilterKey.mapLocations}
      />
    );
  }, [location]);

  return (
    <article className="pb-6">
      <div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="-mt-12 sm:-mt-16">
            <div className="flex justify-between">
              <Tooltip overlay="Click to center the map on this location">
                <LocationMarkerIcon
                  className="h-24 w-24 sm:h-32 sm:w-32 z-10 cursor-pointer"
                  style={{
                    color: (classificationColorMap as any)[
                      transmitScaleClassification
                    ]
                  }}
                  onClick={() => {
                    const coordinates = getLocationCoordinates(
                      props.match.params.id
                    );

                    if (coordinates) {
                      setCenter([coordinates.lng, coordinates.lat]);
                    }
                  }}
                />
              </Tooltip>
              {location && (
                <div className="flex items-center">
                  <DetailsPagination
                    path="locations"
                    selectedItemUuid={location.uuid}
                    data={filteredLocations}
                    idKey="uuid"
                  />
                </div>
              )}
            </div>
            <div className="sm:flex-1 sm:min-w-0 sm:flex sm:items-center sm:justify-end sm:space-x-6 sm:pb-1">
              <div className="mt-6 min-w-0 flex-1">
                <div className="flex justify-between">
                  {location ? (
                    <h1 className="text-md sm:text-2xl font-bold text-gray-900 dark:text-white truncate inline-flex items-center">
                      <span>{location.name}</span>

                      <StatusDot className="ml-3" location={location} />
                    </h1>
                  ) : (
                    <span className="flex w-10 h-6 animate-pulse bg-gray-400 rounded sm:w-40 mt-2" />
                  )}

                  {location ? (
                    <LocationActionsButton location={location} />
                  ) : (
                    <div className="flex flex-shrink-0 w-9 h-9 animate-pulse bg-gray-400 rounded-full"></div>
                  )}
                </div>

                {renderLabels}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 sm:mt-2 2xl:mt-5">
        <div className="sm:border-b sm:border-gray-200 dark:border-gray-500 overflow-auto whitespace-nowrap">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <select
              className="sm:hidden block w-full pl-3 py-2 sm:text-sm rounded-md dark:bg-gray-800"
              onChange={(e) => {
                props.history.push(`${props.match.url}/${e.target.value}`);
              }}
              value={detailsPageMatch?.params.section}
            >
              {tabs.map(({ key, title }) => (
                <option key={key} value={key}>
                  {title}
                </option>
              ))}
            </select>
            <div className="hidden sm:block">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                {tabs.map(({ key, title, count }) => (
                  <NavLink
                    key={key}
                    to={`${props.match.url}/${key}`}
                    activeClassName="border-hotspotty-400 text-gray-900 dark:text-white dark:text-white dark:border-white"
                    inactiveClassName="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                    className="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm"
                  >
                    <span>{title}</span>
                    {!!count && (
                      <span className="bg-gray-200 text-gray-500 group-hover:text-gray-700 dark:text-gray-200 dark:bg-gray-600 dark:group-hover:text-gray-200 dark:group-hover:bg-gray-500 hidden ml-3 py-0.5 px-2.5 rounded-full text-xs font-medium md:inline-block">
                        {count}
                      </span>
                    )}
                  </NavLink>
                ))}
              </nav>
            </div>
          </div>
        </div>
      </div>

      <Switch>
        <Route path={`${props.match.path}/info`} component={LocationInfoTab} />
        <Route
          path={`${props.match.path}/reward-scaling`}
          component={RewardScalingTab}
        />
        <PrivateRoute
          path={`${props.match.path}/contacts`}
          component={LocationContactsTab as any}
        />
        <PrivateRoute
          path={`${props.match.path}/installs`}
          component={LocationInstallsTab as any}
        />
        <PrivateRoute
          path={`${props.match.path}/super-simulations`}
          component={SuperSimulationsTab as any}
        />
        <PrivateRoute
          path={`${props.match.path}/comments`}
          component={LocationCommentsTab as any}
        />
        <Redirect to={`${props.match.path}/super-simulations`} />
      </Switch>
    </article>
  );
};

export default LocationDetails;
